import { createFileRoute } from "@tanstack/react-router";
import { useAdminOrganizationGetCurrentAndActiveSemester } from "@/orval/admin-organization";
import { useState } from "react";
import { useAdminScheduleGetStudentSchedule } from "@/orval/admin-schedule";
import { SharedSchedule } from "@/components/schedule/shared-schedule";
import { Form } from "@/components/form/form";
import { OrganizationStudentsComboboxFormField } from "@/components/form/form-field/combobox/organization-students-combobox-form-field";
import { OrganizationSemestersComboboxFormField } from "@/components/form/form-field/combobox/organization-semesters-combobox-form-field";
import { PortalPageHeader } from "@/components/portal-page-header";
import { optionalSemesterSearchSchema } from "@/lib/route-search";

export const Route = createFileRoute("/_app/organizations/$organizationId/admin/schedule")({
  component: RouteComponent,
  validateSearch: optionalSemesterSearchSchema,
});

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [studentId, setStudentId] = useState<number | undefined>(undefined);
  const { data: adminOrganizationSettings, isLoading: isLoadingSettings } =
    useAdminOrganizationGetCurrentAndActiveSemester(organizationId);

  const effectiveSemesterId =
    search.semesterId ?? adminOrganizationSettings?.activeSemesterId ?? adminOrganizationSettings?.nextSemesterId;

  const { data, isLoading: isLoadingStudents } = useAdminScheduleGetStudentSchedule(
    organizationId,
    effectiveSemesterId!,
    studentId!,
    {
      query: {
        enabled: !!effectiveSemesterId && !!studentId,
      },
    },
  );

  return (
    <div className="p-4 md:p-6">
      <PortalPageHeader
        className="mb-4"
        title="Student Schedule"
        subtitle="View the class schedules for all students in your organization"
      />

      <div className="my-4">
        <Form<{ studentId: number | undefined; semesterId: number | undefined }>
          useFormProps={{
            defaultValues: {
              semesterId: effectiveSemesterId ?? undefined,
              studentId: studentId,
            },
          }}
          containerClassName="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:space-y-0"
          onChange={(data) => {
            setStudentId(data.studentId);
            navigate({ to: ".", search: { semesterId: data.semesterId } });
          }}
        >
          <OrganizationSemestersComboboxFormField
            name="semesterId"
            organizationId={organizationId}
            className="w-full md:w-64"
          />
          <OrganizationStudentsComboboxFormField
            name="studentId"
            organizationId={organizationId}
            className="w-full md:w-64"
          />
        </Form>
      </div>

      {studentId && (
        <SharedSchedule
          isLoading={isLoadingSettings || isLoadingStudents}
          semesterId={effectiveSemesterId}
          students={data ? [data] : undefined}
          showStudentVisibilityToggles={false}
        />
      )}
    </div>
  );
}
